import { action, computed, makeObservable, observable } from 'mobx';
import { Howl } from 'howler';

import { ActiveDrop, DropStartData, FreeDropClaimResponse, FreeDropDrop, FreeDropInfo, FreeDropPize, FreeDropThemeConfig, HTTPResponse, PrizeClaimedData } from '../models';
import { FREE_DROP_COUNTDOWN_NOTIFICATION_GRACE, FreeDropCountdownNotifications, FreeDropThemeConfigs, T } from '../constants';
import { DropStatus, FreeDropNotificationType, FreeDropSounds, FreeDropTheme } from '../enums';
import { freeDropController, HttpResponse, launcherSocket } from '../api';
import { RootStore } from '.';

import ClaimSuccessSound from '../assets/media/free-drop/sounds/claim-success.mp3';
import ClaimErrorSound from '../assets/media/free-drop/sounds/claim-error.mp3';
import DropStartSound from '../assets/media/free-drop/sounds/drop-start.mp3';
import DropSceneSound from '../assets/media/free-drop/sounds/drop-scene.mp3';
import { fadeInPlay, fadeOutStop, getFreeDropReachedLevel, getFreeDropTierGoals, setTranslationValues } from '../helpers';

export class FreeDropStore {

  private rootStore: RootStore;

  public freeDropInfo: FreeDropInfo | null = null;

  public collectedAmount = 0;

  public showAnouncementPopup = false;

  public showDetailsPopup = false;

  public showStartAnimation = false;

  public showProgressBar = true;

  public dropPrizes: Array<FreeDropPize> = [];

  // used when activeDrop exist in freeDropInfo
  public dropPassedTime = 0;

  // { [prizeId]: Response }
  public claimsResponse: Record<number, HttpResponse<HTTPResponse<FreeDropClaimResponse>>> = {};

  // { [prizeId]: prizeAmount }, for show when other user wins
  public claimedPrizes: Record<number, PrizeClaimedData> = {};

  private isLastDrop = false;

  public dropUpdateTrigger = false;

  public sounds: Record<FreeDropSounds, Howl> | null = null;

  // highest level reached in a drop so far, a level is announced only when the player goes above it
  private levelMark: { dropDate: string, level: number } | null = null;

  // `${dropDate}|${threshold}` of the countdown notifications already shown
  private shownCountdownNotifications = new Set<string>();

  // campaign the widget intro was already shown for
  private widgetIntroCampaignId: number | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this, {
      collectedAmount: observable,
      setCollectedAmount: action.bound,

      claimsResponse: observable,
      setClaimsResponse: action.bound,

      dropPrizes: observable,
      setDropPrizes: action.bound,

      freeDropInfo: observable,
      setFreeDropInfo: action.bound,

      showAnouncementPopup: observable,
      setShowAnouncementPopup: action.bound,

      showDetailsPopup: observable,
      setShowDetailsPopup: action.bound,

      showStartAnimation: observable,
      setShowStartAnimation: action.bound,

      showProgressBar: observable,
      setShowProgressBar: action.bound,

      claimedPrizes: observable,
      setClaimedPrizes: action.bound,

      dropUpdateTrigger: observable,
      toggleDropUpdateTrigger: action.bound,

      dropPassedTime: observable,
      setDropPassedTime: action.bound,

      levels: computed,

      sortedDropsByDate: computed,

      themeConfig: computed

    });
  }

  public get levels(): Array<number> {
    if (!this.freeDropInfo) return [];
    return this.freeDropInfo.drops[0].tiers.map((item, i) => i + 1);
  }

  public get sortedDropsByDate(): Array<FreeDropDrop> {
    if (!this.freeDropInfo) return [];
    return [...this.freeDropInfo.drops].sort((a, b) => new Date(a.dropDate).getTime() - new Date(b.dropDate).getTime());
  }

  public get themeConfig(): FreeDropThemeConfig {
    const theme = this.freeDropInfo?.theme;
    // Gift is 0, so the missing theme is checked explicitly; unknown values fall back to Gift too
    return (theme !== undefined && FreeDropThemeConfigs[theme]) || FreeDropThemeConfigs[FreeDropTheme.Gift];
  }

  /**
   * @description the first drop that has not started yet; reads the clock on every call, so during a drop scene
   * it already returns the following drop (unlike the `nextDrop` memo, refreshed only when the scene ends)
   */
  public getUpcomingDrop(): FreeDropDrop | null {
    return this.sortedDropsByDate.find(({ dropDate }) => new Date(dropDate).getTime() > Date.now()) || null;
  }

  public getTierGoals(drop: FreeDropDrop | null): Array<number> {
    if (!drop || !this.freeDropInfo) return [];
    const { currencyCode, rate } = this.rootStore.gameStore;
    return getFreeDropTierGoals(drop, currencyCode, this.freeDropInfo.currency, rate);
  }

  public getDropSceneDuration(prizesCount: number): number {
    if (!this.freeDropInfo) return 0;
    const startAnimDuration = 3000;
    const { visibleDropCountPerIteration, dropClaimIntervalMs } = this.freeDropInfo;
    return startAnimDuration + prizesCount / visibleDropCountPerIteration * dropClaimIntervalMs;
  }

  public setCollectedAmount(value: number): void {
    this.collectedAmount = value;
  }

  public setClaimsResponse(value: Record<number, HttpResponse<HTTPResponse<FreeDropClaimResponse>>>): void {
    this.claimsResponse = value;
  }

  public resetClaimsResponseErrors(): void {
    const updatedClaimsResponse: Record<number, HttpResponse<HTTPResponse<FreeDropClaimResponse>>> = {};
    Object.entries(this.claimsResponse).forEach(([key, value]) => {
      if (!value.data.errorMessage) {
        updatedClaimsResponse[+key] = value;
      }
    });
    this.setClaimsResponse(updatedClaimsResponse);
  }

  public updateClaimsResponse(value: HttpResponse<HTTPResponse<FreeDropClaimResponse>>, prizeId: number): void {
    const data = { ...this.claimsResponse, [prizeId]: value };
    this.setClaimsResponse(data);
  }

  public setFreeDropData(value: FreeDropInfo | null): void {
    if (value?.id !== this.freeDropInfo?.id) {
      this.resetWidgetNotifications();
    }
    this.setFreeDropInfo(value);
    if (value) {
      this.loadSounds();
      this.setShowAnouncementPopup(true);
      if (value.activeDrop) {
        this.handleDropContinue(value.activeDrop);
      }
    }
  }

  public setFreeDropInfo(value: FreeDropInfo | null): void {
    this.freeDropInfo = value;
  }

  public setShowAnouncementPopup(value: boolean): void {
    this.showAnouncementPopup = value;
  }

  public setShowDetailsPopup(value: boolean): void {
    this.showDetailsPopup = value;
  }

  public setShowStartAnimation(value: boolean): void {
    this.showStartAnimation = value;
  }

  public setShowProgressBar(value: boolean): void {
    this.showProgressBar = value;
  }

  public setClaimedPrizes(value: Record<number, PrizeClaimedData>): void {
    this.claimedPrizes = value;
  }

  public setDropPassedTime(value: number): void {
    this.dropPassedTime = value;
  }

  public toggleDropUpdateTrigger(): void {
    this.dropUpdateTrigger = !this.dropUpdateTrigger;
  }

  private updateFinishedFreeDropStatus(): void {
    if (this.freeDropInfo) {
      this.setFreeDropInfo(
        {
          ...this.freeDropInfo,
          drops: this.freeDropInfo.drops.map((drop) => {
            const isFinished = new Date(drop.dropDate).getTime() < Date.now();
            if (isFinished) {
              return {
                ...drop,
                status: DropStatus.Finished
              };
            }
            return drop;
          })
        }
      );
    }
  }

  public handleDropFinish(): void {
    setTimeout(() => {
      this.setDropPrizes([]);
      this.setShowProgressBar(true);
      this.setClaimedPrizes({});
      this.setClaimsResponse({});
      if (this.sounds?.dropScene) {
        fadeOutStop(this.sounds.dropScene);
      }
      this.toggleDropUpdateTrigger();
      this.setShowStartAnimation(false);
      this.updateFinishedFreeDropStatus();
      if (this.isLastDrop) {
        this.setFreeDropInfo(null);
      }
    }, 8000);// 8000 = 5000(anim duration) + 3000(insurance)
    this.setCollectedAmount(0);
  }

  public setDropPrizes(value: Array<FreeDropPize>): void {
    this.dropPrizes = value;
  }

  public handleCampaignStart(value: { campaign: FreeDropInfo }): void {
    if (this.rootStore.modalStore.gameInterruptionModalState.isOpen) return;
    this.setFreeDropData({
      ...value.campaign,
      smallView: true
    });
  }

  public handleCampaignCanceled(campaignId: number): void {
    if (campaignId === this.freeDropInfo?.id) {
      this.setFreeDropInfo(null);
    }
  }

  public handleDropContinue(activeDrop: ActiveDrop): void {
    // 3000: free drop start aimation duration
    const dropPassedTime = Date.now() - new Date(activeDrop.dropDate).getTime() - 3000;

    this.setDropPassedTime(dropPassedTime);
    this.rootStore.freeDropNotificationStore.clearQueue();

    this.sounds?.dropScene.play();
    this.setDropPrizes(activeDrop.prizes);
    this.setShowProgressBar(false);

    const lastDropDate = this.sortedDropsByDate[this.sortedDropsByDate.length - 1].dropDate;
    if (lastDropDate === activeDrop.dropDate) {
      this.isLastDrop = true;
    }

    setTimeout(() => {
      this.handleDropFinish();
    }, this.getDropSceneDuration(activeDrop.prizes.length) - dropPassedTime);
  }

  public hanldeDropStart(value: { drop: DropStartData }): void {
    if (this.rootStore.modalStore.gameInterruptionModalState.isOpen) return;
    this.setShowStartAnimation(true);
    this.setShowProgressBar(false);
    // e.g. a "2 minutes until the drop" held back by an open popup is stale once the drop starts
    this.rootStore.freeDropNotificationStore.clearQueue();

    setTimeout(() => {
      this.sounds?.dropScene.play();
      this.setDropPrizes(value.drop.prizes);
    }, 3000);

    const lastDropDate = this.sortedDropsByDate[this.sortedDropsByDate.length - 1].dropDate;
    if (lastDropDate === value.drop.dropDate) {
      this.isLastDrop = true;
    }

    setTimeout(() => {
      this.handleDropFinish();
    }, this.getDropSceneDuration(value.drop.prizes.length));

    if (this.sounds?.dropStart) {
      fadeInPlay(this.sounds.dropStart, 2000);
    }
  }

  public hanldePrizeClaimed(data: PrizeClaimedData): void {
    this.setClaimedPrizes({ ...this.claimedPrizes, [data.id]: data });
  }

  public handleProgressUpdate(data: { amount: number, campaignId: number }): void {
    if (data.campaignId === this.freeDropInfo?.id) {
      this.setCollectedAmount(data.amount);
      this.trackReachedLevel(data.amount, true);
    }
  }

  /**
   * @description announces "LVL n Unlocked!" when the amount reaches a level of the upcoming drop above the
   * highest one reached so far; the first known amount only sets that mark - its levels were reached earlier.
   * Keeping the mark (instead of comparing with the previous amount) survives out of order updates
   * and the amount reset at drop finish.
   */
  private trackReachedLevel(amount: number, notify: boolean): void {
    const drop = this.getUpcomingDrop();
    if (!drop) return;
    const level = getFreeDropReachedLevel(amount, this.getTierGoals(drop));
    const markedLevel = this.levelMark?.dropDate === drop.dropDate ? this.levelMark.level : 0;
    if (notify && this.levelMark && level > markedLevel) {
      const message = setTranslationValues(this.rootStore.translationsStore.translate(T.FREE_DROP_LEVEL_UNLOCKED_NOTIFICATION), { level });
      this.notify(FreeDropNotificationType.LevelUnlocked, message);
    }
    this.levelMark = { dropDate: drop.dropDate, level: Math.max(level, markedLevel) };
  }

  /**
   * @description called every second by the widget timer; a countdown notification is shown once per drop,
   * only when its threshold passes while the player is there (not to someone joining 10 minutes before the drop)
   */
  public handleCountdownTick(dropDate: string, remainingTime: number): void {
    FreeDropCountdownNotifications.forEach(({ type, threshold, message }) => {
      const key = `${dropDate}|${threshold}`;
      const thresholdPassed = remainingTime > 0 && remainingTime <= threshold && threshold - remainingTime < FREE_DROP_COUNTDOWN_NOTIFICATION_GRACE;
      if (!thresholdPassed || this.shownCountdownNotifications.has(key)) return;
      this.shownCountdownNotifications.add(key);
      this.notify(type, this.rootStore.translationsStore.translate(message));
    });
  }

  /**
   * @description expands the widget once per campaign; the empty message keeps the default progress text
   */
  public showWidgetIntro(): void {
    const campaignId = this.freeDropInfo?.id;
    if (!campaignId || this.widgetIntroCampaignId === campaignId) return;
    this.widgetIntroCampaignId = campaignId;
    this.notify(FreeDropNotificationType.Intro, '');
  }

  // notifications are shown only by the slot line widget
  private notify(type: FreeDropNotificationType, message: string): void {
    if (!this.rootStore.gameStore.isSlotLine) return;
    this.rootStore.freeDropNotificationStore.enqueue(message, type);
  }

  // progress marks and shown notifications belong to one campaign
  private resetWidgetNotifications(): void {
    this.levelMark = null;
    this.shownCountdownNotifications.clear();
    this.rootStore.freeDropNotificationStore.clearQueue();
  }

  public listenEvents(): void {
    launcherSocket.onCampaignStart(this.handleCampaignStart.bind(this));
    launcherSocket.onFreeDropProgressUpdate(this.handleProgressUpdate.bind(this));
    launcherSocket.onFreeDropAnnounce(this.hanldeDropStart.bind(this));
    launcherSocket.onFreeDropPrizeClaimed(this.hanldePrizeClaimed.bind(this));
    launcherSocket.onCampaignCanceled(this.handleCampaignCanceled.bind(this));
  }

  public async getPlayerProgress(freeDropId: number): Promise<void> {
    const data = await freeDropController.getPlayerProgress(freeDropId);
    this.setCollectedAmount(data.collectedAmount);
    this.trackReachedLevel(data.collectedAmount, false);
  }

  public async claim(prizeId: number): Promise<void> {
    this.updateClaimsResponse(new HttpResponse<HTTPResponse<FreeDropClaimResponse>>({ result: { amount: 0, count: 0, id: 0 }, errorMessage: '' }), prizeId);
    const payload = {
      prizeId,
      gameId: this.rootStore.gameStore.gameId,
      campaignId: this.freeDropInfo?.id as number
    };
    const data = await freeDropController.claim(payload);
    if (this.claimsResponse[prizeId]) {
      this.updateClaimsResponse(this.claimsResponse[prizeId].fetched(data), prizeId);
    }
  }

  public loadSounds(): void {
    const dropScene = new Howl({
      src: [DropSceneSound],
      volume: 0.75,
      preload: true,
      html5: true
    });
    const dropStart = new Howl({
      src: [DropStartSound],
      volume: 0.75,
      preload: true,
      html5: true
    });
    const claimError = new Howl({
      src: [ClaimErrorSound],
      volume: 1,
      preload: true,
      html5: true
    });
    const claimSuccess = new Howl({
      src: [ClaimSuccessSound],
      volume: 1,
      preload: true,
      html5: true
    });
    this.sounds = { dropScene, dropStart, claimError, claimSuccess };
  }

  public listenVisibilityChange(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        // 1. LOCKING: Capture current state, then force mute
        this.handleSoundEnabledUpdate(false);
      } else {
        // 2. UNLOCKING: Only unmute if the user wasn't muted before!
        if (this.rootStore.gameStore.soundEnabled) {
          this.handleSoundEnabledUpdate(true);
        }
      }
    });
  }

  public handleSoundEnabledUpdate(soundEnabled: boolean): void {
    if (!this.sounds) return;
    Object.values(this.sounds)
      .forEach((sound) => {
        sound.mute(!soundEnabled)
      });
  }

}
